import { useId, useMemo, useState } from "react";
import {
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
} from "@mui/material";
import { t } from "i18next";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { dayjs } from "@thalamos/common";
import type {
  ExtendedVisionUser,
  IncidentWorkItemWithContext,
} from "@thalamos/vision-common";
import {
  calculateActions,
  combinePlacesOfSafetyAndOfficerPresenceForms,
  getWorkflowTranslationContext,
  IncidentWorkItemIdSchema,
  tk,
} from "@thalamos/vision-common";

import { apiHooks } from "../../api/index.js";
import { Footer } from "../../components/FormBuilder/MultiPageForm/Footer.js";
import type { PlacesOfSafetyProps } from "../../components/PlacesOfSafety/PlacesOfSafety.js";
import { PlacesOfSafety } from "../../components/PlacesOfSafety/PlacesOfSafety.js";
import { useUser } from "../../Context/UserContext.js";
import { FormWrapper } from "../../layout/FormWrapper.js";
import { Error as ErrorComponent } from "../../pages/Error/Error.js";
import { createRoute } from "../../routing/createRoute.js";

export type ReviewPlacesOfSafetyPageProps = Record<string, unknown>;

const extractPlacesOfSafety = (
  incidentWorkItem: IncidentWorkItemWithContext | undefined,
  user: ExtendedVisionUser,
): Omit<PlacesOfSafetyProps, "incidentWorkItem"> => {
  if (incidentWorkItem === undefined) {
    return {
      latestPlaceOfSafety: undefined,
      otherPlacesOfSafety: undefined,
      canEditPOS: false,
    };
  }

  const actions = calculateActions({
    incidentWorkItem,
    user,
    asOfDateTime: dayjs(),
  });

  // Get all of the places of safety forms
  const placesOfSafetyForms: PlacesOfSafetyProps["otherPlacesOfSafety"] =
    combinePlacesOfSafetyAndOfficerPresenceForms(incidentWorkItem)
      .map((posEntries) =>
        posEntries.map((posEntry) => {
          const mainSection =
            posEntry.placeOfSafety.sections.recordPlaceOfSafetyMain;

          const createdDateTime = dayjs.utc(
            posEntry.placeOfSafety.form.createdAt,
          );

          const arrivalSection =
            posEntry.placeOfSafety.sections.recordPlaceOfSafetyArrival;

          const arrivalDateTime = arrivalSection?.arrivalDateTime
            ? dayjs.utc(arrivalSection.arrivalDateTime.value)
            : undefined;

          return {
            name: mainSection.placeOfSafety?.name ?? t("common.unknown"),
            formId: posEntry.placeOfSafety.form.id,
            arrivalDateTime,
            createdDateTime,
          };
        }),
      )
      .unwrapOr([]) as PlacesOfSafetyProps["otherPlacesOfSafety"];

  // Split them into the latest and other places of safety
  const latestPlaceOfSafety = placesOfSafetyForms?.at(0);
  const otherPlacesOfSafety = placesOfSafetyForms?.slice(1, undefined);

  return {
    latestPlaceOfSafety,
    otherPlacesOfSafety,
    canEditPOS: actions.placeOfSafety.canEdit,
  };
};

export function ReviewPlacesOfSafetyPage(
  _props: ReviewPlacesOfSafetyPageProps,
) {
  const params = useParams();

  const incidentWorkItemId = IncidentWorkItemIdSchema.safeParse(params.id);
  if (!incidentWorkItemId.success) {
    throw new Error("Invalid incident work item id");
  }

  const { user, error: userError, loading: userLoading } = useUser();

  const [
    {
      data: incidentWorkItem,
      loading: isIncidentLoading,
      error: incidentError,
    },
  ] = apiHooks.incidentWorkItem.get(incidentWorkItemId.data);

  // Loading guard must run first: while useUser() is loading, `user` is
  // undefined, so checking `user === undefined` as an error before this would
  // render the error page instead of the spinner.
  if (isIncidentLoading || userLoading) {
    return <CircularProgress />;
  }

  if (
    incidentError ||
    userError ||
    user === undefined ||
    user.sessionContext === null
  ) {
    return <ErrorComponent />;
  }

  return (
    incidentWorkItem &&
    user && <Inner incidentWorkItem={incidentWorkItem} user={user} />
  );
}

export type InnerProps = {
  incidentWorkItem: IncidentWorkItemWithContext;
  user: ExtendedVisionUser;
};

export function Inner({ incidentWorkItem, user }: InnerProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const ariaId = useId();

  const [addAnother, setAddAnother] = useState<boolean | null>(null);

  const placesOfSafetyProps = useMemo(
    () => extractPlacesOfSafety(incidentWorkItem, user),
    [incidentWorkItem, user],
  );

  const tOptions = useMemo(
    () => ({ context: getWorkflowTranslationContext({ incidentWorkItem }) }),
    [incidentWorkItem],
  );

  return (
    <FormWrapper>
      <Stack
        sx={{
          gap: 2,
        }}
      >
        <PlacesOfSafety
          {...placesOfSafetyProps}
          incidentWorkItem={incidentWorkItem}
        />
        <Divider />
        <FormControl>
          <FormLabel id={ariaId}>
            {t("reviewPlacesOfSafety.didYouVisitMore", tOptions)}
          </FormLabel>
          <RadioGroup
            aria-labelledby={ariaId}
            value={addAnother}
            onChange={(event) => {
              setAddAnother(event.target.value === "true");
            }}
          >
            <FormControlLabel
              key={"yes"}
              value={true}
              control={<Radio />}
              label={t("common.yes")}
              data-testid={"radio-option-yes"}
            />
            <FormControlLabel
              key={"no"}
              value={false}
              control={<Radio />}
              label={t("common.no")}
              data-testid={"radio-option-no"}
            />
          </RadioGroup>
        </FormControl>
        <Divider />
        <Footer
          onNext={{
            label: addAnother
              ? tk("reviewPlacesOfSafety.add")
              : tk("reviewPlacesOfSafety.next"),
            handler:
              addAnother === null
                ? undefined
                : // eslint-disable-next-line @typescript-eslint/require-await
                  async () => {
                    if (addAnother) {
                      navigate(
                        createRoute.incidentRecordRetrospectivePlaceOfSafetyPage(
                          incidentWorkItem.incidentWorkItem.id,
                        ),
                      );
                    } else {
                      navigate(
                        createRoute.incidentProgressPage(
                          incidentWorkItem.incidentWorkItem.id,
                        ),
                      );
                    }
                  },
          }}
          onCancel={{
            handler: () => {
              navigate(
                createRoute.incidentProgressPage(
                  incidentWorkItem.incidentWorkItem.id,
                ),
              );
            },
          }}
        />
      </Stack>
    </FormWrapper>
  );
}
